import { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Megaphone, Send, UserX, RotateCcw, Loader2, Check, CheckCheck, X, Clock, Ban } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors";

function StatusPill({ status }) {
  const map = {
    READ: { c: "bg-sky-100 text-sky-700 border-sky-200", i: <CheckCheck className="h-3 w-3 text-sky-500" />, t: "Read" },
    DELIVERED: { c: "bg-[#E7FCE8] text-[#075E54] border-[#25D366]/30", i: <CheckCheck className="h-3 w-3" />, t: "Delivered" },
    SENT: { c: "bg-[#F0F2F5] text-[#54656F] border-[#E9EDEF]", i: <Check className="h-3 w-3" />, t: "Sent" },
    QUEUED: { c: "bg-[#F0F2F5] text-[#8696A0] border-[#E9EDEF]", i: <Clock className="h-3 w-3" />, t: "Queued" },
    FAILED: { c: "bg-red-100 text-red-700 border-red-200", i: <X className="h-3 w-3" />, t: "Failed" },
    SKIPPED: { c: "bg-amber-100 text-amber-700 border-amber-200", i: <Ban className="h-3 w-3" />, t: "Opted-out" },
  };
  const s = map[status] || map.QUEUED;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[10px] px-2 py-0.5 rounded-md border ${s.c}`}>
      {s.i} {s.t}
    </span>
  );
}

export default function Broadcasts() {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [optouts, setOptouts] = useState([]);
  const [tpl, setTpl] = useState("");
  const [filter, setFilter] = useState("all");
  const [sending, setSending] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const pollRef = useRef();

  const load = useCallback(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/broadcasts").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/broadcast/optouts").then((r) => setOptouts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try { const r = await api.get(`/broadcasts/${id}`); setDetail(r.data); }
    catch (e) {}
  }, []);

  // Live polling while a broadcast is open
  useEffect(() => {
    if (!activeId) return;
    loadDetail(activeId);
    pollRef.current = setInterval(() => { loadDetail(activeId); load(); }, 2500);
    return () => clearInterval(pollRef.current);
  }, [activeId, loadDetail, load]);

  const send = async () => {
    const t = templates.find((x) => x.template_id === tpl);
    if (!t) { toast.error("Select a template"); return; }
    setSending(true);
    try {
      const r = await api.post("/broadcast/send", { template_name: t.name, language: t.language, body: t.body, category: t.category, category_filter: filter });
      toast.success(`Broadcast started for ${r.data.total} contacts${r.data.skipped_optout ? `, ${r.data.skipped_optout} opted-out skipped` : ""}`);
      setActiveId(r.data.broadcast_id);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Broadcast failed"); }
    finally { setSending(false); }
  };
  const resub = async (id) => { await api.post(`/broadcast/optouts/${id}/resubscribe`); toast.success("Re-subscribed"); load(); };

  const b = detail?.broadcast;
  const recips = detail?.recipients || [];
  const total = b?.total || 0;
  const processed = (b?.sent || 0) + (b?.failed || 0);
  const percent = total > 0 ? Math.round((processed / total) * 100) : (b?.status === "completed" ? 100 : 0);

  return (
    <>
      <PageHeader eyebrow="Campaigns" title="Broadcasts" />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 space-y-6">
        <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4 max-w-2xl">
          <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#075E54]" /><h3 className="font-heading text-sm text-[#111B21]">New Broadcast</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#8696A0]">Template</label>
              <select data-testid="broadcast-template" value={tpl} onChange={(e) => setTpl(e.target.value)} className={inputCls + " mt-1"}>
                <option value="">Select…</option>
                {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name} · {t.category}{t.status ? ` · ${t.status}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#8696A0]">Audience (by lead category)</label>
              <select data-testid="broadcast-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className={inputCls + " mt-1"}>
                <option value="all">All contacts</option>
                <option value="Cold">Cold</option><option value="Warm">Warm</option>
                <option value="Hot">Hot</option><option value="Very Hot">Very Hot</option>
              </select>
            </div>
          </div>
          <p className="text-xs text-[#8696A0]">Marketing broadcasts automatically skip contacts who replied STOP.</p>
          <button data-testid="broadcast-send" onClick={send} disabled={sending}
            className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-semibold px-4 py-2 text-sm transition-colors disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Broadcast
          </button>
        </div>

        {/* Live delivery tracking */}
        {b && (
          <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4 max-w-3xl" data-testid="broadcast-live-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-heading text-sm text-[#111B21] font-mono">{b.template_name}</h3>
                <p className="text-xs text-[#8696A0]">
                  {b.status === "completed" ? "Sending complete · tracking delivery" : "Sending…"} · audience {b.category_filter}
                </p>
              </div>
              <button data-testid="broadcast-close-live" onClick={() => { setActiveId(null); setDetail(null); }}
                className="text-[#8696A0] hover:text-[#111B21]"><X className="h-4 w-4" /></button>
            </div>

            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-[#54656F]">{processed} / {total} processed</span>
                <span className="font-mono text-[#075E54]">{percent}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-[#F0F2F5] overflow-hidden" data-testid="broadcast-progress">
                <div className="h-full bg-[#25D366] transition-all duration-500" style={{ width: `${percent}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                ["Sent", b.sent || 0, "text-[#075E54]"],
                ["Delivered", b.delivered || 0, "text-[#128C7E]"],
                ["Read", b.read || 0, "text-sky-600"],
                ["Failed", b.failed || 0, "text-red-600"],
                ["Skipped", b.skipped_optout || 0, "text-amber-600"],
              ].map(([k, v, c]) => (
                <div key={k} className="border border-[#E9EDEF] rounded-lg p-3 bg-[#F8FAFC]">
                  <p className="font-mono text-[9px] uppercase tracking-wider text-[#8696A0]">{k}</p>
                  <p className={`font-heading text-xl font-bold mt-0.5 ${c}`}>{v}</p>
                </div>
              ))}
            </div>

            <div className="border border-[#E9EDEF] rounded-lg divide-y divide-[#E9EDEF] max-h-72 overflow-y-auto" data-testid="broadcast-recipients">
              {recips.map((r) => (
                <div key={r.recipient_id} className="px-4 py-2.5 flex items-center gap-3" data-testid="broadcast-recipient-row">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#111B21] truncate">{r.name || r.phone}</p>
                    <p className="text-[11px] text-[#8696A0] font-mono">{r.phone}</p>
                  </div>
                  <StatusPill status={r.status} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-[#E9EDEF] bg-white rounded-lg">
            <div className="px-5 py-4 border-b border-[#E9EDEF]"><h3 className="font-heading text-sm text-[#111B21]">History</h3></div>
            <div className="divide-y divide-[#E9EDEF]" data-testid="broadcast-history">
              {history.length === 0 && <p className="px-5 py-8 text-center text-sm text-[#8696A0]">No broadcasts yet.</p>}
              {history.map((h) => (
                <button key={h.broadcast_id} data-testid="broadcast-history-item" onClick={() => setActiveId(h.broadcast_id)}
                  className={`w-full text-left px-5 py-3 text-sm transition-colors ${activeId === h.broadcast_id ? "bg-[#E7FCE8]" : "hover:bg-[#F0F2F5]"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[#111B21] font-mono truncate">{h.template_name} <span className="text-[#8696A0]">· {h.category_filter}</span></p>
                    {h.status !== "completed" && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-md bg-[#DCF8C6] text-[#075E54] border border-[#25D366]/40">sending…</span>}
                  </div>
                  <p className="text-xs text-[#8696A0]">sent {h.sent || 0} · delivered {h.delivered || 0} · read {h.read || 0} · failed {h.failed || 0} · skipped {h.skipped_optout || 0} · {new Date(h.created_at).toLocaleString()}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="border border-[#E9EDEF] bg-white rounded-lg">
            <div className="px-5 py-4 border-b border-[#E9EDEF] flex items-center gap-2"><UserX className="h-4 w-4 text-red-600" /><h3 className="font-heading text-sm text-[#111B21]">Opted-out (replied STOP)</h3></div>
            <div className="divide-y divide-[#E9EDEF]" data-testid="optout-list">
              {optouts.length === 0 && <p className="px-5 py-8 text-center text-sm text-[#8696A0]">No opt-outs.</p>}
              {optouts.map((c) => (
                <div key={c.contact_id} className="px-5 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#111B21]">{c.name}</p>
                    <p className="text-xs text-[#8696A0] font-mono">{c.phone} · {c.opt_out_at ? new Date(c.opt_out_at).toLocaleString() : ""}</p>
                  </div>
                  <button data-testid={`resub-${c.contact_id}`} onClick={() => resub(c.contact_id)}
                    className="flex items-center gap-1.5 text-xs border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1.5 transition-colors">
                    <RotateCcw className="h-3.5 w-3.5" /> Re-add
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
